import React, { useState } from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const COLLAPSED_ARRAY_LENGTH = 10;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function childEntries(value: unknown): [string, unknown][] | null {
  if (Array.isArray(value)) {
    return value.map((item, index) => [String(index), item]);
  }
  if (isRecord(value)) {
    return Object.entries(value);
  }
  return null;
}

function JsonPrimitive({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground">null</span>;
  }
  if (typeof value === "string") {
    return <span className="break-all text-success-soft-foreground">"{value}"</span>;
  }
  if (typeof value === "number") {
    return <span className="tabular-nums text-info-soft-foreground">{value}</span>;
  }
  if (typeof value === "boolean") {
    return <span className="text-warning-soft-foreground">{String(value)}</span>;
  }
  return <span>{String(value)}</span>;
}

function JsonNode({ label, value }: { label: string; value: unknown }) {
  const [expanded, setExpanded] = useState(() => !Array.isArray(value) || value.length <= COLLAPSED_ARRAY_LENGTH);

  const entries = childEntries(value);

  if (!entries) {
    return (
      <div className="flex gap-1.5 py-0.5 pl-5">
        <span className="shrink-0 text-foreground">{label}:</span>
        <JsonPrimitive value={value} />
      </div>
    );
  }

  const summary = Array.isArray(value) ? `[${entries.length}]` : `{${entries.length}}`;

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((current) => !current)}
        className="flex items-center gap-1.5 rounded py-0.5 text-left hover:bg-muted"
      >
        <ChevronRight
          className={cn("h-3.5 w-3.5 shrink-0 text-muted-foreground", expanded && "rotate-90")}
        />
        <span className="text-foreground">{label}:</span>
        <span className="text-muted-foreground">{summary}</span>
      </button>
      {expanded && (
        <div className="ml-[7px] border-l border-border pl-2">
          {entries.map(([childLabel, childValue]) => (
            <JsonNode key={childLabel} label={childLabel} value={childValue} />
          ))}
        </div>
      )}
    </div>
  );
}

export function JsonTree({ value, className }: { value: unknown; className?: string }) {
  const entries = childEntries(value);

  return (
    <div className={cn("overflow-x-auto rounded-lg border bg-muted/40 p-3 font-mono text-xs", className)}>
      {entries ? (
        entries.map(([label, childValue]) => <JsonNode key={label} label={label} value={childValue} />)
      ) : (
        <JsonPrimitive value={value} />
      )}
    </div>
  );
}
